'use client';

import { useRef, useState } from 'react';
import type { z } from 'zod';
import type { FieldIssue } from '@/app/lib/forms/tracking/types';
import type { FieldRefs, FormState, FormValues, UseFormOptions } from '@/app/lib/forms/types/form.types';
import { scrollIntoViewIfNeeded } from '@/app/utils/scrollIntoViewIfNeed';
const FOCUSABLE_SELECTOR = 'input:not([disabled]):not([type="hidden"]), textarea:not([disabled]), select:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Custom hook for managing form state, validation, and submission with Zod schema validation.
 * @param schema - Zod schema for form validation
 * @param initialValues - Initial form values
 * @param validators - Custom field validators
 * @returns Form state, validation, and submission functions
 *
 * Read more: [Storybook docs: `useForm`](https://tis-web-story-book.netlify.app/?path=/docs/hooks-useform--documentation)
 */
export const useForm = <T extends z.ZodType,>({
  schema,
  getSchemaForParse,
  initialValues = {} as FormValues<T>,
  validators = {},
  tracking
}: UseFormOptions<T>) => {
  const fieldRefs = useRef<FieldRefs<FormValues<T>>>({});
  const initialValuesRef = useRef(initialValues);
  const [values, setValues] = useState<FormValues<T>>(initialValues as FormValues<T>);
  // Latest values for tracking, which runs outside the state updaters.
  const valuesRef = useRef<FormValues<T>>(values);
  const blurListeners = useRef<Partial<Record<keyof FormValues<T>, {
    el: HTMLElement;
    fn: () => void;
  }>>>({});
  const [state, setState] = useState<FormState<FormValues<T>> & {
    isSubmitted: boolean;
  }>({
    errors: {},
    isSubmitting: false,
    isSubmitted: false
  });
  const resolveParseSchema = (vals: FormValues<T>) => {
    if (getSchemaForParse) {
      return getSchemaForParse(vals) as T;
    }
    return schema;
  };
  const validateForm = (vals_0: FormValues<T>) => {
    const result = resolveParseSchema(vals_0).safeParse(vals_0);
    const emptyErrors: Partial<Record<keyof FormValues<T>, string>> = {};
    if (result.success) {
      return {
        isValid: true,
        errors: emptyErrors,
        issues: {}
      };
    }
    const errors: Partial<Record<keyof FormValues<T>, string>> = {};
    // Same first issue per field, with its code: tracking reports the error type.
    const issues: Partial<Record<string, FieldIssue>> = {};
    for (const issue of result.error.issues) {
      const field = issue.path[0] as keyof FormValues<T> | undefined;
      if (field && !errors[field]) {
        errors[field] = issue.message;
        issues[field as string] = {
          message: issue.message,
          code: issue.code
        };
      }
    }
    return {
      isValid: false,
      errors,
      issues
    };
  };
  const scrollToFirstError = (errors_0: Partial<Record<keyof FormValues<T>, string>>) => {
    const element = (Object.keys(errors_0) as (keyof FormValues<T>)[]).reduce<HTMLElement | null>((first, field_0) => {
      const el = fieldRefs.current[field_0];
      if (!el) return first;
      if (!first) return el;
      return first.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING ? el : first;
    }, null);
    if (!element) return;
    const isElementFocusable = element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement || element instanceof HTMLSelectElement || element instanceof HTMLButtonElement || element.tabIndex >= 0;
    const focusTarget = isElementFocusable ? element : element.querySelector(FOCUSABLE_SELECTOR) as HTMLElement | null;
    focusTarget?.focus();
    scrollIntoViewIfNeeded(element, null, {
      block: 'center'
    });
  };
  const validate = () => {
    const {
      isValid,
      errors: errors_1,
      issues: issues_0
    } = validateForm(values);
    tracking?.onSubmit({
      isValid,
      issues: issues_0,
      values
    });
    setState(prev => ({
      ...prev,
      isSubmitted: true,
      errors: errors_1
    }));
    if (!isValid) scrollToFirstError(errors_1);
    return isValid;
  };
  const handleSubmit = (onValid: (data: FormValues<T>) => void | Promise<void>) => {
    return async (e?: React.FormEvent) => {
      if (e && e.cancelable) e.preventDefault();
      const {
        isValid: isValid_0,
        errors: errors_2,
        issues: issues_1
      } = validateForm(values);
      tracking?.onSubmit({
        isValid: isValid_0,
        issues: issues_1,
        values
      });
      setState(prev_0 => ({
        ...prev_0,
        isSubmitting: isValid_0,
        isSubmitted: true,
        errors: errors_2
      }));
      if (!isValid_0) {
        scrollToFirstError(errors_2);
        return;
      }
      try {
        await onValid(values);
      } finally {
        setState(prev => ({
          ...prev,
          isSubmitting: false
        }));
      }
    };
  };

  /** `track: false` for values the page fills in (e.g. from the URL), not the customer. */
  const setValue = <K extends keyof FormValues<T>,>(field_1: K, value: FormValues<T>[K], {
    track = true
  }: {
    track?: boolean;
  } = {}) => {
    const validator = validators[field_1 as string];
    let transformedValue = value;
    if (validator?.transform) {
      transformedValue = validator.transform(value);
    }
    if (!track) {
      valuesRef.current = {
        ...valuesRef.current,
        [field_1]: transformedValue
      };
    } else if (
    // Skip writes that leave the value as it was, e.g. a form clearing an
    // already-empty dependent field: those are not the customer's input.
    tracking?.enabled && !Object.is(valuesRef.current[field_1], transformedValue)) {
      const nextValues = {
        ...valuesRef.current,
        [field_1]: transformedValue
      };
      valuesRef.current = nextValues;
      tracking.onFieldChange(field_1 as string, transformedValue, validateForm(nextValues).issues[field_1 as string]);
    }
    setValues(prevValues => {
      const newValues = {
        ...prevValues,
        [field_1]: transformedValue
      };

      // Check for custom onChange validator first
      if (typeof validator?.onChange === 'function') {
        const error = validator.onChange(transformedValue, newValues);
        setState(prev_1 => {
          const newErrors = {
            ...prev_1.errors
          };
          if (error) newErrors[field_1] = error;else delete newErrors[field_1];
          return {
            ...prev_1,
            errors: newErrors
          };
        });
      } else {
        // If no custom validator, only validate after form has been submitted
        setState(prev_2 => {
          if (!prev_2.isSubmitted) {
            // Before submission, don't validate
            return prev_2;
          }

          // After submission, validate on change
          const {
            errors: schemaErrors
          } = validateForm(newValues);
          const fieldError = schemaErrors[field_1];
          const newErrors_0 = {
            ...prev_2.errors
          };
          if (fieldError) {
            newErrors_0[field_1] = fieldError;
          } else {
            delete newErrors_0[field_1];
          }
          return {
            ...prev_2,
            errors: newErrors_0
          };
        });
      }
      return newValues;
    });
  };
  const setError = <K extends keyof FormValues<T>,>(field_2: K, message: string) => {
    setState(prev_3 => ({
      ...prev_3,
      errors: {
        ...prev_3.errors,
        [field_2]: message
      }
    }));
  };
  const clearErrors = <K extends keyof FormValues<T>,>(field_3?: K) => {
    setState(prev_4 => {
      if (field_3) {
        const newErrors_1 = {
          ...prev_4.errors
        };
        delete newErrors_1[field_3];
        return {
          ...prev_4,
          errors: newErrors_1
        };
      }
      return {
        ...prev_4,
        errors: {}
      };
    });
  };
  const reset = () => {
    valuesRef.current = initialValuesRef.current as FormValues<T>;
    setValues(initialValuesRef.current as FormValues<T>);
    setState({
      errors: {},
      isSubmitting: false,
      isSubmitted: false
    });
  };

  // Blur tracking rides on the registered element; `focusout` bubbles, so a
  // wrapper element works as well as the input itself.
  const trackBlur = <K extends keyof FormValues<T>,>(field_4: K, el_0: HTMLElement | null) => {
    const current = blurListeners.current[field_4];
    if (current?.el === el_0) return;
    current?.el.removeEventListener('focusout', current.fn);
    delete blurListeners.current[field_4];
    if (!el_0 || !tracking?.enabled) return;
    const fn = () => {
      const latest = valuesRef.current;
      tracking.onFieldBlur(field_4 as string, latest[field_4], validateForm(latest).issues[field_4 as string]);
    };
    el_0.addEventListener('focusout', fn);
    blurListeners.current[field_4] = {
      el: el_0,
      fn
    };
  };
  const registerRef = <K extends keyof FormValues<T>,>(field_5: K) => {
    return (el_1: HTMLElement | null) => {
      trackBlur(field_5, el_1);
      if (el_1) {
        fieldRefs.current[field_5] = el_1;
        return;
      }
      delete fieldRefs.current[field_5];
    };
  };
  return {
    validate,
    handleSubmit,
    setValue,
    setError,
    clearErrors,
    reset,
    registerRef,
    values,
    formState: state,
    fieldRefs
  };
};